import { useState } from 'react';

import type { TeamOut, UserListItem } from '@solar/api-client';
import { ApiError } from '@solar/api-client';
import { ErrorBox, Spinner, useAsync } from '@solar/ui';

import { api } from '../api';
import { Modal } from '../Modal';

/**
 * 설치 팀 정보 — 레거시 WebService/company/team 파리티.
 *
 * 팀은 회사 하위 리소스라 회사를 먼저 고른다. 업체 관리자에게는 서버가
 * 자기 회사만 내려주므로 셀렉트에 한 개만 보인다. 기사 배속(engineer_ids)은
 * 전체 동기화 — 체크를 빼고 저장하면 그 기사는 팀에서 해제된다.
 */
export function TeamManage() {
  const companies = useAsync(() => api.listCompanies({ size: 200 }), []);
  const [companyId, setCompanyId] = useState<number | ''>('');
  const [selected, setSelected] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  // 회사가 하나뿐이면(업체 관리자) 자동 선택한다.
  const items = companies.data?.items ?? [];
  const effectiveCompanyId =
    companyId !== '' ? companyId : items.length === 1 ? (items[0]?.id ?? '') : '';

  const teams = useAsync(
    () =>
      effectiveCompanyId === ''
        ? Promise.resolve(null)
        : api.listTeams(effectiveCompanyId),
    [effectiveCompanyId],
  );

  // 기사 배속 편집용 — 회사 소속 설치기사(role=4). 서버 격리 후 회사로 다시 거른다.
  const engineers = useAsync(() => api.listUsers({ role: 4, size: 200 }), []);
  const companyEngineers = (engineers.data?.items ?? []).filter(
    (u) => effectiveCompanyId !== '' && u.build_company_id === effectiveCompanyId,
  );

  const row = teams.data?.find((t) => t.id === selected) ?? null;

  return (
    <div className="admin__body">
      <section className="admin__list">
        <div className="toolbar">
          {companies.error && <ErrorBox message={companies.error} onRetry={companies.reload} />}
          <select
            value={effectiveCompanyId}
            onChange={(e) => {
              setCompanyId(e.target.value === '' ? '' : Number(e.target.value));
              setSelected(null);
            }}
          >
            <option value="">설치 회사 선택</option>
            {items.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="primary create"
            disabled={effectiveCompanyId === ''}
            onClick={() => setCreateOpen(true)}
          >
            팀 등록
          </button>
        </div>

        {effectiveCompanyId === '' && <p className="muted">설치 회사를 선택하세요.</p>}
        {teams.loading && effectiveCompanyId !== '' && <Spinner />}
        {teams.error && <ErrorBox message={teams.error} onRetry={teams.reload} />}

        {teams.data && (
          <table className="grid">
            <thead>
              <tr>
                <th>설치팀 명</th>
                <th className="num">소속 설치기사 수</th>
                <th>메모</th>
              </tr>
            </thead>
            <tbody>
              {teams.data.length === 0 && (
                <tr>
                  <td colSpan={3} className="muted">
                    등록된 팀이 없습니다.
                  </td>
                </tr>
              )}
              {teams.data.map((t) => (
                <tr
                  key={t.id}
                  className={t.id === selected ? 'selected' : ''}
                  onClick={() => setSelected(t.id)}
                >
                  <td>{t.name}</td>
                  <td className="num">{t.engineer_ids.length}</td>
                  <td className="addr">{t.memo ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <aside className="admin__aside">
        {row === null || effectiveCompanyId === '' ? (
          <p className="muted">팀을 선택하세요.</p>
        ) : (
          <TeamEdit
            key={row.id}
            companyId={effectiveCompanyId}
            row={row}
            engineers={companyEngineers}
            onClose={() => setSelected(null)}
            onChanged={() => teams.reload()}
            onDeleted={() => {
              setSelected(null);
              teams.reload();
            }}
          />
        )}
      </aside>

      {createOpen && effectiveCompanyId !== '' && (
        <TeamCreateModal
          companyId={effectiveCompanyId}
          engineers={companyEngineers}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            teams.reload();
          }}
        />
      )}
    </div>
  );
}

/** 기사 배속 체크리스트 — engineer_ids 편집 공용. */
function EngineerPicker({
  engineers,
  checked,
  onToggle,
}: {
  engineers: UserListItem[];
  checked: ReadonlySet<number>;
  onToggle: (id: number) => void;
}) {
  if (engineers.length === 0) {
    return <p className="muted">이 회사 소속 설치기사가 없습니다.</p>;
  }
  return (
    <div className="engineer-picker">
      {engineers.map((u) => (
        <label key={u.id} className="check">
          <input type="checkbox" checked={checked.has(u.id)} onChange={() => onToggle(u.id)} />
          {u.name ?? u.user_id} ({u.user_id})
        </label>
      ))}
    </div>
  );
}

function TeamEdit({
  companyId,
  row,
  engineers,
  onClose,
  onChanged,
  onDeleted,
}: {
  companyId: number;
  row: TeamOut;
  engineers: UserListItem[];
  onClose: () => void;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const [name, setName] = useState(row.name);
  const [memo, setMemo] = useState(row.memo ?? '');
  const [checked, setChecked] = useState<ReadonlySet<number>>(new Set(row.engineer_ids));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  function toggle(id: number) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function save() {
    if (!name.trim()) {
      setError('팀 이름을 입력해 주세요.');
      return;
    }
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      await api.updateTeam(companyId, row.id, {
        name: name.trim(),
        memo: memo.trim() || undefined,
        engineer_ids: [...checked],
      });
      setOk('저장했습니다.');
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await api.deleteTeam(companyId, row.id);
      onDeleted();
    } catch (err) {
      // 배정 진행 중 주문이 있는 팀 등 삭제 가드 409 메시지를 그대로 보여준다.
      setError(err instanceof ApiError ? err.message : '삭제하지 못했습니다.');
      setConfirmDelete(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="detail">
      <div className="detail__head">
        <h2>{row.name}</h2>
        <button type="button" className="link" onClick={onClose}>
          닫기
        </button>
      </div>

      <section className="panel form-grid">
        <label className="f wide">
          팀 이름 *
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="f wide">
          메모
          <input value={memo} onChange={(e) => setMemo(e.target.value)} />
        </label>
      </section>

      <h3 className="section">설치기사 배속 ({checked.size}명)</h3>
      <section className="panel">
        <EngineerPicker engineers={engineers} checked={checked} onToggle={toggle} />
        <p className="muted">체크를 빼고 저장하면 해당 기사는 팀에서 해제됩니다.</p>
      </section>

      {error && <p className="error">{error}</p>}
      {ok && <p className="ok">{ok}</p>}

      <div className="row form-actions">
        <button type="button" disabled={busy} onClick={() => setConfirmDelete(true)}>
          삭제
        </button>
        <button type="button" className="primary" disabled={busy} onClick={() => void save()}>
          {busy ? '저장 중…' : '저장'}
        </button>
      </div>

      {confirmDelete && (
        <Modal title="팀 삭제" onClose={() => setConfirmDelete(false)}>
          <p>
            <strong>{row.name}</strong> 팀을 삭제합니다. 계속할까요?
          </p>
          <div className="row">
            <button type="button" onClick={() => setConfirmDelete(false)}>
              취소
            </button>
            <button type="button" className="primary" disabled={busy} onClick={() => void remove()}>
              삭제
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function TeamCreateModal({
  companyId,
  engineers,
  onClose,
  onCreated,
}: {
  companyId: number;
  engineers: UserListItem[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [memo, setMemo] = useState('');
  const [checked, setChecked] = useState<ReadonlySet<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggle(id: number) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function submit() {
    if (!name.trim()) {
      setError('팀 이름을 입력해 주세요.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.createTeam(companyId, {
        name: name.trim(),
        memo: memo.trim() || undefined,
        engineer_ids: checked.size > 0 ? [...checked] : undefined,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '등록하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="설치 팀 등록" onClose={onClose}>
      <label className="labeled">
        팀 이름 *
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="labeled">
        메모
        <input value={memo} onChange={(e) => setMemo(e.target.value)} />
      </label>
      <EngineerPicker engineers={engineers} checked={checked} onToggle={toggle} />

      {error && <p className="error">{error}</p>}
      <div className="row">
        <button type="button" onClick={onClose}>
          취소
        </button>
        <button type="button" className="primary" disabled={busy} onClick={() => void submit()}>
          {busy ? '등록 중…' : '등록'}
        </button>
      </div>
    </Modal>
  );
}
